import React, { useState, useEffect } from "react";
import { siteConfig } from "../../config/siteConfig";

const Navbar = ({ onLoginClick, onRegisterClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { brand, hero } = siteConfig;

  const navLinks = [
    { href: "#features", label: "Funktioner" },
    { href: "#pricing", label: "Priser" },
    { href: "#testimonials", label: "Detta ingår" },
    { href: "#faq", label: "FAQ" },
  ];

  const closeMenu = () => setIsMenuOpen(false);

  // Etiquetas accesibles del botón hamburguesa: config-driven con fallback seguro
  // (si la niche no define brand.menu*, se usa el copy por defecto en es-ES).
  const openMenuLabel = brand.menuOpenLabel || "Öppna menyn";
  const closeMenuLabel = brand.menuCloseLabel || "Stäng menyn";

  // Bloquea el scroll del fondo mientras el menú móvil está abierto (mismo patrón
  // que DemoModal) para que el panel no quede sobre contenido que sigue moviéndose.
  useEffect(() => {
    if (!isMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  // Escape cierra el menú móvil (patrón de accesibilidad de diálogo/disclosure):
  // solo se registra el listener mientras el menú está abierto.
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  // Cierra el menú al pasar a viewport lg (mismo umbral que Header/Sidebar/Layout
  // de la app) para evitar estados colgados y un salto de experiencia en tablets.
  useEffect(() => {
    if (!isMenuOpen) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => {
      if (e.matches) setIsMenuOpen(false);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [isMenuOpen]);

  // Único listener de scroll pasivo + rAF-throttled (el mismo patrón ligero que
  // comparte StickyCTA): al pasar el umbral, el header conmuta a estado "scrolled"
  // (.elev-2 + hairline) manteniendo el glass-header. Solo toca clases → 0 CLS.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 12);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      aria-label="Principal"
      className={`fixed top-0 left-0 right-0 z-50 glass-header transition-shadow duration-300 ${
        isScrolled
          ? "elev-2 border-b border-slate-900/10"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-indigo-700 to-sky-600 p-2.5 rounded-xl elev-2">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                {brand.namePrefix}
                <span className="text-indigo-700">{brand.nameSuffix}</span>
              </span>
              {brand.subBrand && (
                <span className="text-[12px] font-bold text-indigo-700 mt-0.5 tracking-tighter">
                  {brand.subBrand}
                </span>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                className="nav-underline text-sm font-semibold text-slate-600 hover:text-indigo-700 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <button
              onClick={onLoginClick}
              className="px-5 py-2.5 text-sm font-semibold text-slate-700 hover:text-indigo-700 transition-colors"
            >
              Logga in
            </button>
            <button
              onClick={onRegisterClick}
              className="btn-primary glow-cta px-5 py-2.5 text-sm"
            >
              {hero.primaryCta}
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? closeMenuLabel : openMenuLabel}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            className="lg:hidden flex items-center justify-center min-w-[44px] min-h-[44px] p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <span aria-hidden="true" className="relative block w-6 h-6">
              <span
                className={`absolute left-1/2 top-1/2 block h-0.5 w-5 -translate-x-1/2 rounded-full bg-slate-600 transition-transform duration-200 ease-out ${
                  isMenuOpen ? "rotate-45" : "-translate-y-1.5"
                }`}
              />
              <span
                className={`absolute left-1/2 top-1/2 block h-0.5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-600 transition-opacity duration-200 ease-out ${
                  isMenuOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute left-1/2 top-1/2 block h-0.5 w-5 -translate-x-1/2 rounded-full bg-slate-600 transition-transform duration-200 ease-out ${
                  isMenuOpen ? "-rotate-45" : "translate-y-1.5"
                }`}
              />
            </span>
          </button>
        </div>

        {isMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden py-4 border-t border-slate-200 animate-slideDown"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link, idx) => (
                <a
                  key={idx}
                  href={link.href}
                  onClick={closeMenu}
                  className="flex items-center text-sm font-semibold text-slate-600 hover:text-indigo-700 min-h-[44px] py-2"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    onLoginClick();
                    closeMenu();
                  }}
                  className="btn-ghost px-4 py-2.5 min-h-[44px] text-sm"
                >
                  Logga in
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onRegisterClick();
                    closeMenu();
                  }}
                  className="btn-primary glow-cta px-4 py-2.5 min-h-[44px] text-sm"
                >
                  {hero.primaryCta}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
